import React, { useEffect, useState, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import {
  fetchUsers,
  searchUsers,
  fetchUserDetails,
  fetchAvailableLevels,
  clearSearchResults,
  clearSelectedUser,
  setSearchMode,
  setFilters,
  setPageSize,
  setCustomPageSize,
  clearFilters,
  selectUsers,
  selectSearchResults,
  selectSelectedUser,
  selectUsersLoading,
  selectUsersSearching,
  selectUsersFetchingDetails,
  selectUsersError,
  selectUsersSearchError,
  selectUsersDetailsError,
  selectUsersPagination,
  selectUsersSearchPagination,
  selectUsersSearchMode,
  selectUsersFilters,
  selectAvailableLevels,
  selectIsLoadingLevels,
} from "../../RTK/users/GetAllUsers";
import {
  Search,
  SlidersHorizontal,
  Users as UsersIcon,
  UserCheck,
  UserX,
  User,
  ChevronDown,
  Loader2,
  Wallet,
  TrendingUp,
  Bell,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Package,
  Hash,
  Filter,
  Settings,
  X,
  Edit3,
  Infinity,
  Check,
} from "lucide-react";
import UserDetailsPopup from "./UserDetailsPopup";
import NotificationPopup from "./popups/NotificationPopup ";
import { sendNotificationAllUser } from "../../RTK/users/SendNotificationAllUsers";
import { useNavigate } from "react-router-dom";

import { usePermissions } from "../../contexts/PermissionsContext";
import { ProtectedComponent } from "../../components/ProtectedComponent/ProtectedComponent";
import { Lock, Percent} from "lucide-react";


// Loading Spinner Component
const LoadingSpinner = memo(({ text }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px] gap-2">
    <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
    <span className="text-gray-400">{text}</span>
  </div>
));

// Empty State Component
const EmptyState = memo(() => (
  <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
    <UsersIcon className="w-12 h-12 text-gray-500" />
    <p className="text-gray-500 text-lg">لا يوجد مستخدمون</p>
  </div>
));

// Error State Component
const ErrorState = memo(({ message }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
    <AlertCircle className="w-12 h-12 text-red-500" />
    <p className="text-red-500 text-lg">{message}</p>
  </div>
));

// Custom Page Size Input Component
const CustomPageSizeInput = memo(({ currentPageSize, onPageSizeChange, maxUsers }) => {
  const [customSize, setCustomSize] = useState(currentPageSize.toString());
  const [isEditing, setIsEditing] = useState(false);

  const maxAllowed = Math.min(5000, maxUsers || 5000);

  const handleSubmit = (e) => {
    e.preventDefault();
    const size = parseInt(customSize);
    if (size > 0 && size <= maxAllowed) {
      onPageSizeChange(size);
      setIsEditing(false);
      toast.success(`تم تحديد العدد إلى ${size}`);
    } else {
      toast.error(`يجب أن يكون العدد بين 1 و ${maxAllowed}`);
      setCustomSize(currentPageSize.toString());
    }
  };

  const handleCancel = () => {
    setCustomSize(currentPageSize.toString());
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-gray-300 text-sm">
          عدد النتائج: {currentPageSize >= maxUsers ? "الكل" : currentPageSize}
        </span>
        <button
          onClick={() => setIsEditing(true)}
          className="p-1 text-gray-400 hover:text-white transition-colors"
          title="تعديل العدد"
        >
          <Edit3 className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2">
      <input
        type="number"
        min="1"
        max={maxAllowed}
        value={customSize}
        onChange={(e) => setCustomSize(e.target.value)}
        className="w-24 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-sm"
        autoFocus
        placeholder="أدخل العدد"
      />
      <button
        type="submit"
        className="px-2 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors flex items-center gap-1"
      >
        <Check className="w-3 h-3" />
        حفظ
      </button>
      <button
        type="button"
        onClick={handleCancel}
        className="px-2 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
      <span className="text-xs text-gray-500">الحد الأقصى: {maxAllowed}</span>
    </form>
  );
});

// User Card Component
const UserCard = memo(({ user, onClick, canViewDetails }) => {
  const navigate = useNavigate();
  
  return (
    <div
      onClick={canViewDetails ? () => onClick(user.id) : undefined}
      className={`relative bg-gray-900 rounded-xl p-6 border border-gray-800 transition-all duration-300 backdrop-blur-sm ${
        canViewDetails 
          ? "cursor-pointer hover:border-yellow-500/50 hover:bg-gray-800/50" 
          : "cursor-not-allowed opacity-70"
      }`}
    >
      {/* ✅ Custom Pricing Icon - Top Right */}
      {canViewDetails && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/user/${user.id}/custom-pricing`);
          }}
          className="absolute top-3 right-3 p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors z-10 group"
          title="قواعد التسعير المخصصة"
        >
          <Percent className="w-4 h-4" />
          <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            قواعد التسعير
          </span>
        </button>
      )}

      {/* ✅ أيقونة قفل لو مافيش صلاحية */}
      {!canViewDetails && (
        <div className="absolute top-2 left-2 bg-slate-700/90 p-1.5 rounded-lg z-10">
          <Lock className="w-3 h-3 text-slate-400" />
        </div>
      )}

      <div className="flex flex-col items-center">
        {/* User Avatar & Status */}
        <div className="relative mb-4">
          <div
            className={`p-4 rounded-full bg-gradient-to-br 
            ${
              user.is_active
                ? "from-emerald-500/20 to-emerald-500/10"
                : "from-rose-500/20 to-rose-500/10"
            } 
            transition-colors duration-300`}
          >
            <User
              className={`w-12 h-12 ${
                user.is_active ? "text-emerald-500" : "text-rose-500"
              }`}
            />
          </div>
          <div
            className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-gray-800
            ${user.is_active ? "bg-emerald-500" : "bg-rose-500"}`}
          />
        </div>

        {/* User Info */}
        <h3 className="text-white font-semibold text-lg mb-1 text-center">
          {user.name || user.full_name}
        </h3>
        <p className="text-gray-400 text-sm mb-2">@{user.username}</p>

        {/* Enhanced Level Badge */}
        {user.level_details ? (
          <div
            className="mt-2 px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-lg"
            style={{ 
              backgroundColor: `${user.level_details.hex_color}20`,
              borderColor: `${user.level_details.hex_color}50`,
              border: '1px solid'
            }}
          >
            {user.level_details.icon && (
              <img 
                src={user.level_details.icon} 
                alt={user.level}
                className="w-5 h-5 rounded"
              />
            )}
            <span 
              className="font-medium"
              style={{ color: user.level_details.hex_color }}
            >
              {user.level}   
            </span>
            {user.level_details.profit_percentage && (
              <span 
                className="text-xs font-medium px-2 py-1 rounded-full"
                style={{ 
                  backgroundColor: `${user.level_details.hex_color}30`,
                  color: user.level_details.hex_color
                }}
              >
                {user.level_details.profit_percentage}%
              </span>
            )}
          </div>
        ) : (
          <div className="mt-2 px-4 py-2 rounded-lg text-sm flex items-center gap-2 bg-gray-700/50 border border-gray-600">
            <TrendingUp className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400 font-medium">
              {user.level || "غير محدد"}
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="mt-6 w-full grid grid-cols-2 gap-4">
          <div className="flex flex-col items-center p-3 rounded-lg bg-gray-800/30">
            <Wallet className="w-5 h-5 text-yellow-500 mb-2" />
            <span className="text-white font-medium">
              ${user.wallet_balance}
            </span>
            <span className="text-xs text-gray-400">الرصيد</span>
          </div>
          <div className="flex flex-col items-center p-3 rounded-lg bg-gray-800/30">
            <UserCheck className="w-5 h-5 text-emerald-500 mb-2" />
            <span className="text-white font-medium text-xs">
              {user.is_verified ? "مُؤكد" : "غير مُؤكد"}
            </span>
            <span className="text-xs text-gray-400">الحالة</span>
          </div>
        </div>
      </div>
    </div>
  );
});

// Pagination Component
const Pagination = memo(({ pagination, onPageChange, isLoading }) => {
  const { currentPage, totalPages, hasNext, hasPrevious, totalCount, pageSize } = pagination;

  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    let end = Math.min(totalPages, start + showPages - 1);
    
    if (end - start < showPages - 1) {
      start = Math.max(1, end - showPages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="text-gray-400 text-sm">
          عرض النتائج من {((currentPage - 1) * pageSize) + 1} إلى{' '}
          {Math.min(currentPage * pageSize, totalCount)} من أصل {totalCount}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrevious || isLoading}
            className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              disabled={isLoading}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                page === currentPage
                  ? 'bg-yellow-500 text-black'
                  : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              } disabled:cursor-not-allowed`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNext || isLoading}
            className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
});

// Main Component
function Users() {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions(); 
  
  // ✅ فحص الصلاحيات
  const canViewUserDetails = hasPermission("view_user_details");
  const canSendNotifications = hasPermission("send_notifications");

  const users = useSelector(selectUsers);
  const searchResults = useSelector(selectSearchResults);
  const selectedUser = useSelector(selectSelectedUser);
  const isLoading = useSelector(selectUsersLoading);
  const isSearching = useSelector(selectUsersSearching);
  const isFetchingDetails = useSelector(selectUsersFetchingDetails);
  const error = useSelector(selectUsersError);
  const searchError = useSelector(selectUsersSearchError);
  const detailsError = useSelector(selectUsersDetailsError);
  const pagination = useSelector(selectUsersPagination);
  const isSearchMode = useSelector(selectUsersSearchMode);
  const filters = useSelector(selectUsersFilters);
  const availableLevels = useSelector(selectAvailableLevels);
  const isLoadingLevels = useSelector(selectIsLoadingLevels);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("username");
  const [searchPage, setSearchPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isNotificationPopup, setIsNotificationPopup] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Local level filter state (static filtering)
  const [selectedLevelFilter, setSelectedLevelFilter] = useState("");
const navigate = useNavigate();

  // Search type options
  const searchTypeOptions = [
    { value: "username", label: "اسم المستخدم" },
    { value: "email", label: "البريد الإلكتروني" },
    { value: "phone_number", label: "رقم الهاتف" },
  ];

  // Page size options with "All" option
  const predefinedPageSizes = [10, 20, 30, 50, 100, 500, 1000];

  // Initial fetch (without level filter in API)
  useEffect(() => {
    // Fetch available levels for filtering
    dispatch(fetchAvailableLevels());
    
    if (!searchQuery) {
      dispatch(fetchUsers({ 
        page: 1, 
        pageSize: filters.pageSize,
        status: filters.status
      }))
        .unwrap()
        .catch((error) => {
          console.error("Error fetching users:", error);
          toast.error("فشل في تحميل المستخدمين");
        });
    }
  }, [dispatch, filters.pageSize, filters.status]);

  // Debounced search (without level filter in API)
  useEffect(() => {
    const handler = debounce((query, type) => {
      if (query.trim()) {
        setSearchPage(1);
        dispatch(searchUsers({ 
          query: query.trim(),
          searchType: type
        }))
          .unwrap()
          .catch((error) => {
            console.error("Search error:", error);
            toast.error(error || "فشل في البحث");
          });
      } else {
        dispatch(clearSearchResults());
        setSearchPage(1);
      }
    }, 500);

    handler(searchQuery, searchType);
    return () => handler.cancel();
  }, [searchQuery, searchType, dispatch]);

  const handleUserClick = (profile_id) => {
    if (!canViewUserDetails) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل المستخدمين 🔒");
      return;
    }
    navigate(`/user/${profile_id}`);
  };

  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
    if (!isSearchMode) {
      dispatch(fetchUsers({ 
        page: 1, 
        pageSize: newPageSize,
        status: filters.status
      }));
    }
    
    // Show appropriate message
    const totalUsers = pagination.totalCount || 0;
    if (newPageSize >= totalUsers) {
      toast.success("تم تحديد العرض لجميع المستخدمين");
    } else {
      toast.success(`تم تحديد العرض إلى ${newPageSize} مستخدم`);
    }
  };

  const handleCustomPageSizeChange = (customSize) => {
    dispatch(setCustomPageSize(customSize));
    if (!isSearchMode) {
      dispatch(fetchUsers({ 
        page: 1, 
        pageSize: customSize,
        status: filters.status
      }));
    }
  };

  const handleShowAllUsers = () => {
    // Get the total count from API and use it as page size
    const totalUsers = pagination.totalCount || 0;
    if (totalUsers > 0) {
      handlePageSizeChange(totalUsers);
    } else {
      toast.error("لا يمكن تحديد العدد الكلي، جاري المحاولة...");
      // Fallback to a large number
      handlePageSizeChange(9999);
    }
  };

  // Static level filter handler (no API call)
  const handleLevelFilter = (level) => {
    setSelectedLevelFilter(level);
    setFilter("all"); // Reset other filters when level is applied
  };

  const handleStatusFilter = (status) => {
    dispatch(setFilters({ status }));
    if (!searchQuery.trim()) {
      dispatch(fetchUsers({ 
        page: 1, 
        pageSize: filters.pageSize,
        status
      }));
    }
  };

  const handleFilterReset = () => {
    setFilter("all");
    setSearchQuery("");
    setSearchType("username");
    setSearchPage(1);
    setSelectedLevelFilter(""); // Reset level filter
    dispatch(clearFilters());
    dispatch(clearSearchResults());
  };

  const handlePageChange = (page) => {
    if (isSearchMode && searchQuery.trim()) {
      setSearchPage(page);
    } else {
      dispatch(fetchUsers({ 
        page, 
        pageSize: filters.pageSize,
        status: filters.status
      }));
    }
  };

  // Client-side pagination for search results
  const getSearchPagination = (searchData, currentPage, pageSize) => {
    const usersArray = searchData?.data || (Array.isArray(searchData) ? searchData : []);
    const totalCount = usersArray.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = usersArray.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      pagination: {
        currentPage,
        pageSize,
        totalCount,
        totalPages,
        hasNext: currentPage < totalPages,
        hasPrevious: currentPage > 1
      }
    };
  };

  // Determine which data to show
  let currentData;
  let currentPagination;
  let usersToShow;

  if (isSearchMode && searchResults) {
    const searchPaginationResult = getSearchPagination(searchResults, searchPage, filters.pageSize);
    usersToShow = searchPaginationResult.data;
    currentPagination = searchPaginationResult.pagination;
    currentData = searchResults;
  } else {
    currentData = users;
    currentPagination = pagination;
    usersToShow = users?.data || (Array.isArray(users) ? users : []);
  }

  // Apply static filters to the data
  let filteredUsers = usersToShow || [];

  // Apply level filter statically (client-side)
  if (selectedLevelFilter) {
    filteredUsers = filteredUsers.filter((user) => user.level === selectedLevelFilter);
  }

  // Apply other UI filters (active, inactive, verified, etc.)
  filteredUsers = filteredUsers.filter((user) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "active" && user.is_active) ||
      (filter === "nonActive" && !user.is_active) ||
      (filter === "verified" && user.is_verified) ||
      (filter === "unverified" && !user.is_verified) ||
      (filter === "wallet_balance" && parseFloat(user.wallet_balance) > 0);

    return matchesFilter;
  });

  // Apply filters to the data for button counts (after level filter is applied)
  let usersForCounts = usersToShow || [];
  if (selectedLevelFilter) {
    usersForCounts = usersForCounts.filter((user) => user.level === selectedLevelFilter);
  }

  // Calculate level counts from current data
  const getLevelCounts = () => {
    const allCurrentUsers = usersToShow || [];
    const levelCounts = {};
    
    // Count all users
    levelCounts.all = allCurrentUsers.length;
    
    // Count users by level
    allCurrentUsers.forEach(user => {
      const levelName = user.level || "غير محدد";
      levelCounts[levelName] = (levelCounts[levelName] || 0) + 1;
    });
    
    return levelCounts;
  };

  const levelCounts = getLevelCounts();

  const filterButtons = [
    { 
      key: "all", 
      label: "الكل", 
      count: usersForCounts?.length || 0,
      color: "bg-blue-500/10 text-blue-400 border-blue-500/30" 
    },
    { 
      key: "active", 
      label: "نشط", 
      count: usersForCounts?.filter((u) => u.is_active).length || 0,
      color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" 
    },
    { 
      key: "nonActive", 
      label: "غير نشط", 
      count: usersForCounts?.filter((u) => !u.is_active).length || 0,
      color: "bg-rose-500/10 text-rose-400 border-rose-500/30" 
    },
    { 
      key: "verified", 
      label: "مُؤكد", 
      count: usersForCounts?.filter((u) => u.is_verified).length || 0,
      color: "bg-purple-500/10 text-purple-400 border-purple-500/30" 
    },
    { 
      key: "unverified", 
      label: "غير مُؤكد", 
      count: usersForCounts?.filter((u) => !u.is_verified).length || 0,
      color: "bg-orange-500/10 text-orange-400 border-orange-500/30" 
    },
    { 
      key: "wallet_balance", 
      label: "مستخدم فعال", 
      count: usersForCounts?.filter((u) => parseFloat(u.wallet_balance) > 0).length || 0,
      color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" 
    },
  ];

  // Calculate total user count from API
  const totalUserCount = isSearchMode 
    ? (searchResults?.data?.length || (Array.isArray(searchResults) ? searchResults.length : 0))
    : (pagination.totalCount || users?.count || 0);

  // Calculate stats from current data (API response or filtered data)
  const allUsersData = usersToShow || [];
  
  const stats = [
    {
      title: "إجمالي عدد المستخدمين",
      count: totalUserCount,
      icon: <Hash className="w-6 h-6 text-yellow-500" />,
      color: "bg-yellow-500/10",
    },
    {
      title: "المستخدمين النشطين",
      count: allUsersData.filter((u) => u.is_active).length || 0,
      icon: <UserCheck className="w-6 h-6 text-emerald-500" />,
      color: "bg-emerald-500/10",
    },
    {
      title: "المستخدمين غير النشطين",
      count: allUsersData.filter((u) => !u.is_active).length || 0,
      icon: <UserX className="w-6 h-6 text-rose-500" />,
      color: "bg-rose-500/10",
    },
    {
      title: "المستخدمين المعروضين",
      count: filteredUsers?.length || 0,
      icon: <UsersIcon className="w-6 h-6 text-blue-500" />,
      color: "bg-blue-500/10",
    },
  ];

  const { isLoading: isNotificationLoading } = useSelector((state) => state.notificationsAllUsers);

  const handleNotificationSubmit = async (formData) => {
    try {
      await dispatch(
        sendNotificationAllUser({
          notificationData: formData,
        })
      ).unwrap();

      toast.success("تم إرسال الإشعار بنجاح");
      setIsNotificationPopup(false);
    } catch (error) {
      toast.error(error || "فشل في إرسال الإشعار");
      console.error("Notification Error:", error);
    }
  };

  // Check if showing all users
  const isShowingAll = filters.pageSize >= totalUserCount && totalUserCount > 0;

  return (
    <div className="p-6 space-y-8" dir="rtl">
      {/* Advanced Filters Panel */}
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-4 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Settings className="w-5 h-5" />
            الفلاتر والإعدادات
          </h3>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center gap-2 px-3 py-1 rounded-lg bg-gray-800 text-gray-400 hover:text-white transition-colors"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {showAdvancedFilters ? "إخفاء" : "إظهار"}
            <ChevronDown className={`w-4 h-4 transition-transform ${showAdvancedFilters ? "rotate-180" : ""}`} />
          </button>
        </div>

        {showAdvancedFilters && (
          <div className="space-y-4">
            {/* Page Size Selector */}
            <div className="flex items-center gap-4 flex-wrap">
              <label className="text-gray-300 text-sm font-medium">عدد النتائج في الصفحة:</label>
              
              {/* Predefined sizes */}
              <div className="flex gap-2 flex-wrap">
                {predefinedPageSizes.map(size => (
                  <button
                    key={size}
                    onClick={() => handlePageSizeChange(size)}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors border ${
                      filters.pageSize === size
                        ? "bg-blue-500/20 text-blue-400 border-blue-500/50"
                        : "bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700"
                    }`}
                  >
                    {size}
                  </button>
                ))}
                
                {/* Show All Button */}
                <button
                  onClick={handleShowAllUsers}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors border flex items-center gap-1 ${
                    isShowingAll
                      ? "bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 border-purple-500/50"
                      : "bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700 hover:text-purple-400"
                  }`}
                  title={`عرض جميع المستخدمين (${totalUserCount} مستخدم)`}
                >
                  <Infinity className="w-4 h-4" />
                  الكل ({totalUserCount})
                </button>
              </div>
            </div>

            {/* Custom Page Size Input */}
            <div className="flex items-center gap-4">
              <label className="text-gray-300 text-sm font-medium">أو أدخل عدد مخصص:</label>
              <CustomPageSizeInput 
                currentPageSize={filters.pageSize}
                onPageSizeChange={handleCustomPageSizeChange}
                maxUsers={totalUserCount}
              />
            </div>

            {/* Warning for large numbers */}
            {filters.pageSize > 1000 && (
              <div className="flex items-center gap-2 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-400" />
                <span className="text-orange-300 text-sm">
                  تحذير: عرض عدد كبير من المستخدمين قد يؤثر على أداء الصفحة
                </span>
              </div>
            )}

            {/* Show All Info */}
            {isShowingAll && (
              <div className="flex items-center gap-2 p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                <Infinity className="w-5 h-5 text-purple-400" />
                <span className="text-purple-300 text-sm">
                  يتم عرض جميع المستخدمين ({totalUserCount} مستخدم)
                </span>
              </div>
            )}

            {/* Level Filter (Static) */}
            <div>
              <label className="text-gray-300 text-sm font-medium mb-2 block">
                فلتر المستويات (محلي):
                {isLoadingLevels && (
                  <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />
                )}
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleLevelFilter("")}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors border flex items-center gap-2 ${
                    selectedLevelFilter === ""
                      ? "bg-blue-500/20 text-blue-400 border-blue-500/50"
                      : "bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700"
                  }`}
                >
                  جميع المستويات
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    selectedLevelFilter === ""
                      ? 'bg-white/20 text-blue-300'
                      : 'bg-gray-600/50 text-gray-300'
                  }`}>
                    {levelCounts.all || 0}
                  </span>
                </button>
                {availableLevels && availableLevels.length > 0 && availableLevels.map((level) => {
                  const count = levelCounts[level.name] || 0;
                  return (
                    <button
                      key={level.id}
                      onClick={() => handleLevelFilter(level.name)}
                      className={`px-3 py-1 rounded-lg text-sm transition-colors border flex items-center gap-2 ${
                        selectedLevelFilter === level.name
                          ? `border-opacity-50`
                          : "bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700"
                      }`}
                      style={{
                        backgroundColor: selectedLevelFilter === level.name ? `${level.hex_color}20` : undefined,
                        borderColor: selectedLevelFilter === level.name ? `${level.hex_color}50` : undefined,
                        color: selectedLevelFilter === level.name ? level.hex_color : undefined,
                      }}
                    >
                      {level.icon && (
                        <img src={level.icon} alt={level.name} className="w-4 h-4 rounded" />
                      )}
                      {level.name}
                      {level.profit_percentage && (
                        <span className="text-xs opacity-75">
                          {level.profit_percentage}%
                        </span>
                      )}
                      <span 
                        className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          selectedLevelFilter === level.name
                            ? 'bg-white/20'
                            : 'bg-gray-600/50 text-gray-300'
                        }`}
                        style={{
                          backgroundColor: selectedLevelFilter === level.name ? `${level.hex_color}30` : undefined,
                          color: selectedLevelFilter === level.name ? level.hex_color : undefined,
                        }}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active Filters Display */}
            {(selectedLevelFilter || filters.status !== "all" || isShowingAll) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-400 text-sm">الفلاتر النشطة:</span>
                {selectedLevelFilter && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-xs">
                    المستوى: {selectedLevelFilter} ({levelCounts[selectedLevelFilter] || 0})
                    <button
                      onClick={() => handleLevelFilter("")}
                      className="hover:bg-blue-500/30 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {isShowingAll && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-xs">
                    <Infinity className="w-3 h-3" />
                    عرض الكل ({totalUserCount})
                  </span>
                )}
                <button
                  onClick={handleFilterReset}
                  className="text-xs text-red-400 hover:text-red-300 underline"
                >
                  مسح جميع الفلاتر
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Top Filter Buttons */}
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-4 border border-gray-800">
        <div className="flex flex-wrap items-center justify-center gap-3">
          {filterButtons.map((filterBtn) => (
            <button
              key={filterBtn.key}
              onClick={() => setFilter(filterBtn.key)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border
                ${filter === filterBtn.key 
                  ? `${filterBtn.color} scale-105 shadow-lg` 
                  : 'bg-gray-800/50 text-gray-400 border-gray-700 hover:bg-gray-700/50 hover:text-gray-300'
                }
              `}
            >
              <span className="flex items-center gap-2">
                {filterBtn.label}
                <span className={`
                  px-2 py-1 rounded-full text-xs font-bold
                  ${filter === filterBtn.key 
                    ? 'bg-white/20' 
                    : 'bg-gray-600/50'
                  }
                `}>
                  {filterBtn.count}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-gray-900 rounded-xl p-6 shadow-lg border border-gray-800 hover:border-gray-700 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">{stat.title}</p>
                <h4 className="text-2xl font-bold text-white mt-2">
                  {stat.count.toLocaleString()}
                </h4>
              </div>
              <div className={`p-3 rounded-lg ${stat.color}`}>{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-4 w-full md:w-auto">
          {/* Search Type Dropdown */}
          <div className="relative">
            <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="bg-gray-900 border border-gray-800 rounded-lg py-2 pr-10 pl-4 text-white focus:outline-none focus:border-yellow-500/50 transition-colors appearance-none cursor-pointer"
            >
              {searchTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
          </div>

          {/* Search Input */}
          <div className="relative flex-1 md:w-96">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder={`بحث في ${searchTypeOptions.find(opt => opt.value === searchType)?.label || 'اسم المستخدم'}...`}
              className="w-full bg-gray-900 border border-gray-800 rounded-lg py-2 pr-10 pl-4 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500/50 transition-colors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {isSearching && (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-500 animate-spin" />
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {canSendNotifications ? (
            <button 
              onClick={() => setIsNotificationPopup(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors bg-gray-800 text-gray-300 hover:bg-gray-600 hover:text-white active:scale-95 focus:outline-none focus:ring focus:ring-yellow-500"
            >
              <Bell className="text-yellow-500" size={20} />
              <span className="md:inline">
                ارسال اشعار لجميع المستخدمين
              </span>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
              <Lock className="w-5 h-5 text-slate-500" />
              <span className="text-slate-400 text-sm hidden md:inline">
                ليس لديك صلاحية إرسال الإشعارات
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Search Results Info */}
      {isSearchMode && searchResults && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-400">
            <Search className="w-5 h-5" />
            <span>
              تم العثور على {searchResults?.data?.length || (Array.isArray(searchResults) ? searchResults.length : 0)} نتيجة للبحث عن "{searchQuery}" في {searchTypeOptions.find(opt => opt.value === searchType)?.label}
              {selectedLevelFilter && ` - المستوى: ${selectedLevelFilter}`}
            </span>
          </div>
        </div>
      )}

      {/* Level Filter Info */}
      {selectedLevelFilter && (
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-purple-400">
            <Filter className="w-5 h-5" />
            <span>
              عرض المستخدمين من مستوى "{selectedLevelFilter}" فقط - {levelCounts[selectedLevelFilter] || 0} مستخدم
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      {(isLoading || isSearching) ? (
        <LoadingSpinner text={isSearching ? "جاري البحث في جميع المستخدمين..." : "جاري تحميل المستخدمين..."} />
      ) : error || searchError ? (
        <ErrorState message={error || searchError} />
      ) : filteredUsers.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredUsers.map((user) => (
              <UserCard key={user.id} user={user} onClick={handleUserClick} canViewDetails={canViewUserDetails} />
            ))}
          </div>

          {/* Pagination - only show if not showing all users */}
          {!isShowingAll && (
            <Pagination
              pagination={currentPagination}
              onPageChange={handlePageChange}
              isLoading={isLoading || isSearching}
            />
          )}
        </>
      )}

      {/* User Details Popup */}
      {/* {isPopupVisible && selectedUser && (
        <UserDetailsPopup
          user={selectedUser}
          onClose={() => setIsPopupVisible(false)}
        />
      )} */}

      {/* Notification Popup */}
      {isNotificationPopup && canSendNotifications && (
        <NotificationPopup
          onClose={() => setIsNotificationPopup(false)}
          onSubmit={handleNotificationSubmit}
          isLoading={isNotificationLoading}
        />
      )}

      {/* Loading Details Overlay */}
      {/* {isFetchingDetails && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
            <span className="text-white">جاري تحميل التفاصيل...</span>
          </div>
        </div>
      )} */}
    </div>
  );
}

export default memo(Users);